/**
 * The role permission matrix from BOAS-7641 (wiki page 1718779974).
 *
 * Apps receive only the role and the business type; this file is the fixed map
 * they consult. Owners and admins hold every permission in both business types,
 * so they never appear in the table below - `can()` short-circuits for them.
 *
 * "main outlet" means the permission is limited to the outlet the member is
 * attached to rather than granted outright.
 */
export type TeamRole = "manager" | "accountant" | "cashier" | "admin";
export type BusinessType = "MS" | "IND";
/**
 * How far a permission reaches:
 *  - "all"         everywhere the business owns
 *  - "main-outlet" only when the member is attached to the main outlet
 *  - "linked"      only to the outlet(s) the member is mapped to
 *  - "own"         only rows the member created (a cashier's own sales)
 *
 * `can()` treats every non-"none" scope as granted; screens that have to
 * narrow a list ask `scopeFor()` instead.
 */
export type OutletScope = "all" | "main-outlet" | "linked" | "own" | "none";

export type Permission =
  /** The dashboard. */
  | "home:view"
  /** The balance figures on the dashboard and in the outlet switcher. */
  | "balance:view"
  /** The account number on the dashboard. */
  | "account:view"
  | "transactions:view"
  | "payment:collect"
  /** Creating payment links, the Payment Links tab on Collect Payment. */
  | "payment-link:create"
  | "transfer:initiate"
  | "bills:pay"
  | "pos:request"
  | "pos:manage"
  | "dispute:manage"
  | "refund:initiate"
  | "inventory:manage"
  | "inventory:stock"
  | "analytics:view"
  /** The Analytics tab (charts); a cashier sees Sales History only. */
  | "analytics:charts"
  /** The Send money and Top 10 beneficiaries charts on Analytics. */
  | "analytics:send-money"
  | "sale:undo"
  | "outlet:create"
  | "outlet:read"
  | "outlet:manage"
  | "outlet:withdraw"
  | "vaas:manage"
  | "settings:profile"
  | "settings:preferences"
  | "settings:statement"
  | "team:manage";

type Grant = OutletScope;

/**
 * Everything BOAS-7641 grants a non-admin role. A role missing from an entry
 * does not hold that permission at all; the story lists the same breakdown for
 * multi-signatory and for individual/sole proprietorship except where noted
 * (VAAS and fund transfer, which the individual table does not mention).
 */
const GRANTS: Record<
  Permission,
  Partial<Record<BusinessType, Partial<Record<TeamRole, Grant>>>>
> = {
  "home:view": {
    MS: { manager: "linked", accountant: "linked", cashier: "own" },
    IND: { manager: "linked", accountant: "linked", cashier: "own" },
  },
  // Cashiers see no balance.
  "balance:view": {
    MS: { manager: "linked", accountant: "linked" },
    IND: { manager: "linked", accountant: "linked" },
  },
  // Cashiers see no account number.
  "account:view": {
    MS: { manager: "linked", accountant: "linked" },
    IND: { manager: "linked", accountant: "linked" },
  },
  "transactions:view": {
    MS: { manager: "linked", accountant: "linked", cashier: "own" },
    IND: { manager: "linked", accountant: "linked", cashier: "own" },
  },
  // "Can collect payment via inventory / Direct payment collection" - Cashier.
  "payment:collect": { MS: { cashier: "linked" }, IND: { cashier: "linked" } },
  // Cashiers collect payments but do not create payment links.
  "payment-link:create": { MS: {}, IND: {} },
  // "...if linked to main wallet but requires approval (Except for individual
  // and sole proprietorship)" - so only multi-signatory, only main outlet.
  "transfer:initiate": {
    MS: { manager: "main-outlet", accountant: "main-outlet" },
    IND: {},
  },
  "bills:pay": { MS: {}, IND: {} },
  "pos:request": { MS: {}, IND: {} },
  // "POS" is listed under Manager's Outlet section in both tables - returning
  // or managing the device at their own outlet, not requesting a new one.
  "pos:manage": {
    MS: { manager: "linked" },
    IND: { manager: "linked" },
  },
  // Refunds are not in the story's breakdown for any non-admin role.
  "refund:initiate": { MS: {}, IND: {} },
  "dispute:manage": { MS: { manager: "linked" }, IND: { manager: "linked" } },
  // Manager updates stock; creating and deleting products stays with Admin.
  "inventory:manage": { MS: {}, IND: {} },
  "inventory:stock": { MS: { manager: "linked" }, IND: { manager: "linked" } },
  "analytics:view": {
    MS: { manager: "linked", accountant: "linked", cashier: "own" },
    IND: { manager: "linked", accountant: "linked", cashier: "own" },
  },
  // Cashiers see Sales History only.
  "analytics:charts": {
    MS: { manager: "linked", accountant: "linked" },
    IND: { manager: "linked", accountant: "linked" },
  },
  // Cashiers do not send money.
  "analytics:send-money": {
    MS: { manager: "linked", accountant: "linked" },
    IND: { manager: "linked", accountant: "linked" },
  },
  "sale:undo": { MS: { manager: "linked" }, IND: { manager: "linked" } },
  "outlet:create": { MS: {}, IND: {} },
  "outlet:read": { MS: { manager: "linked" }, IND: { manager: "linked" } },
  "outlet:manage": { MS: { manager: "linked" }, IND: { manager: "linked" } },
  "outlet:withdraw": {
    MS: { manager: "linked" },
    IND: { manager: "linked" },
  },
  "vaas:manage": { MS: { manager: "linked" }, IND: {} },
  "settings:profile": {
    MS: { manager: "linked", accountant: "linked", cashier: "linked" },
    IND: { manager: "linked", accountant: "linked", cashier: "linked" },
  },
  "settings:preferences": { MS: {}, IND: {} },
  // Cashiers cannot generate statements.
  "settings:statement": {
    MS: { manager: "linked", accountant: "linked" },
    IND: { manager: "linked", accountant: "linked" },
  },
  "team:manage": { MS: { manager: "linked" }, IND: { manager: "linked" } },
};

/** How far the caller's permission reaches, or "none" when they lack it. */
export const scopeFor = (
  role: TeamRole,
  businessType: BusinessType,
  permission: Permission
): OutletScope => {
  if (role === "admin") return "all";
  return GRANTS[permission]?.[businessType]?.[role] ?? "none";
};

export const can = (
  role: TeamRole,
  businessType: BusinessType,
  permission: Permission
): boolean => scopeFor(role, businessType, permission) !== "none";
